/**
 * The selected villager card: body, inventory, goal, task and build progress,
 * then the decision — move / action / goal distributions with the chosen
 * option highlighted, the three Nouls as gauges, and the exact state Jev saw.
 *
 * Built once per villager and updated in place so the bars animate instead of
 * blinking; rows appear and disappear as the offered options change.
 */
import {
  ACTIONS,
  GOALS,
  MOVES,
  RESOURCES,
  carried,
  type Action,
  type Goal,
  type Move,
  type Resource,
  type Villager,
} from '@village/shared';
import {
  ACTION_DOING,
  ACTION_LABEL,
  BLUEPRINT_LABEL,
  GOAL_LABEL,
  MOVE_LABEL,
  RESOURCE_COLOR,
  RESOURCE_LABEL,
  confidenceLevel,
  el,
  headingLabel,
  noulKeys,
  noulLabel,
  pct,
} from './format';
import { asDecision, asVillager, wasSampled, type DecisionView } from '../contract';

type Row = { li: HTMLElement; fill: HTMLElement; value: HTMLElement; label: HTMLElement };

/** One horizontal distribution: a titled list of option bars with a confidence pill. */
class BarGroup {
  readonly root = el('section', 'group');
  private list = el('ul', 'probs');
  private pill = el('span', 'pill');
  private sampledPill = el('span', 'pill is-sampled', 'sampled from top 3');
  private rows = new Map<string, Row>();

  constructor(title: string) {
    const head = el('header', 'group-head');
    const pills = el('span', 'group-pills');
    this.sampledPill.title = 'confidence below 0.5 — picked at random, weighted by probability';
    this.sampledPill.hidden = true;
    pills.append(this.sampledPill, this.pill);
    head.append(el('h4', undefined, title), pills);
    this.root.append(head, this.list);
  }

  update(
    probs: Record<string, number | undefined>,
    chosen: string,
    labelOf: (key: string) => string,
    confidence: number,
    order: readonly string[],
    sampled = false,
  ): void {
    this.pill.textContent = `confidence ${pct(confidence)}`;
    this.pill.dataset.level = confidenceLevel(confidence);
    this.sampledPill.hidden = !sampled;

    const offered = order.filter((key) => probs[key] !== undefined || key === chosen);
    const values = offered.map((key) => [key, probs[key] ?? 0] as const).sort((a, b) => b[1] - a[1]);
    const max = Math.max(0.0001, ...values.map(([, p]) => p));

    for (const [key] of values) {
      if (this.rows.has(key)) continue;
      const li = el('li', 'prob');
      const label = el('span', 'prob-label', labelOf(key));
      const track = el('span', 'prob-track');
      const fill = el('span', 'prob-fill');
      track.append(fill);
      const value = el('span', 'prob-value');
      li.append(label, track, value);
      this.rows.set(key, { li, fill, value, label });
    }

    const live = new Set(values.map(([key]) => key));
    for (const [key, row] of this.rows) {
      if (live.has(key)) continue;
      row.li.remove();
      this.rows.delete(key);
    }

    for (const [key, p] of values) {
      const row = this.rows.get(key)!;
      row.label.textContent = labelOf(key);
      row.fill.style.width = `${(p / max) * 100}%`;
      row.value.textContent = pct(p);
      row.li.classList.toggle('is-chosen', key === chosen);
      row.li.classList.toggle('is-sampled', sampled && key === chosen);
      this.list.append(row.li); // keeps the list sorted by probability
    }
  }
}

/** A Noul answer with its 0.6 gate marked on the track. */
class Gauge {
  readonly root = el('div', 'gauge');
  private fill = el('span', 'gauge-fill');
  private value = el('span', 'gauge-value');

  constructor(label: string) {
    const track = el('span', 'gauge-track');
    track.append(el('span', 'gauge-gate'), this.fill);
    this.root.append(el('span', 'gauge-label', label), track, this.value);
  }

  set(v: number): void {
    const n = Math.max(0, Math.min(1, v));
    this.fill.style.width = `${n * 100}%`;
    this.value.textContent = pct(n);
    this.root.classList.toggle('is-over', n >= 0.6);
  }
}

export class VillagerCard {
  readonly root = el('div', 'vc');
  readonly villagerId: string;

  private activity = el('p', 'vc-activity');
  private goalChip = el('span', 'goal-chip');
  private bars: Record<'hunger' | 'energy', { fill: HTMLElement; value: HTMLElement; track: HTMLElement }>;
  private invChips = new Map<Resource, { chip: HTMLElement; count: HTMLElement }>();
  private carryLabel = el('span', 'carry-total');
  private taskBox = el('div', 'progress-box');
  private taskLabel = el('span', 'progress-label');
  private taskValue = el('span', 'progress-value');
  private taskFill = el('span', 'progress-fill');
  private buildBox = el('div', 'progress-box');
  private buildLabel = el('span', 'progress-label');
  private buildValue = el('span', 'progress-value');
  private buildFill = el('span', 'progress-fill');
  private homeLine = el('p', 'home-line');

  private decision = el('section', 'decision');
  private decisionMeta = el('div', 'decision-meta');
  private fallback = el('p', 'fallback', 'Rules brain (API unavailable) — Jev could not be reached, or the daily cap is spent.');
  private heading = el('p', 'heading-line');
  private moveGroup = new BarGroup('Move');
  private actionGroup = new BarGroup('Action');
  private goalGroup = new BarGroup('Goal');
  private gauges = new Map<string, Gauge>();
  private noulsBox = el('div', 'nouls');
  private stateDetails = el('details', 'jev-state');
  private statePre = el('pre');

  constructor(v: Villager) {
    this.villagerId = v.id;

    const head = el('div', 'vc-head');
    const avatar = el('span', 'vc-avatar', v.name.slice(0, 1));
    avatar.style.setProperty('--c', v.color);
    const titles = el('div', 'vc-titles');
    titles.append(el('h2', 'vc-name', v.name), this.activity, this.heading);
    head.append(avatar, titles, this.goalChip);

    const traits = el('ul', 'traits');
    for (const t of v.traits) traits.append(el('li', 'chip', t));

    const body = el('div', 'needs');
    const mk = (label: string, color: string) => {
      const row = el('div', 'need');
      const track = el('span', 'need-track');
      const fill = el('span', 'need-fill');
      fill.style.background = color;
      track.append(fill);
      const value = el('span', 'need-value');
      row.append(el('span', 'need-label', label), track, value);
      body.append(row);
      return { fill, value, track };
    };
    this.bars = { hunger: mk('Hunger', 'var(--peach)'), energy: mk('Energy', 'var(--butter)') };

    const inv = el('div', 'inventory');
    for (const res of RESOURCES) {
      const chip = el('span', 'inv-chip');
      chip.style.setProperty('--c', RESOURCE_COLOR[res]);
      const count = el('strong', 'inv-count', '0');
      chip.append(el('span', 'inv-dot'), el('span', 'inv-name', RESOURCE_LABEL[res]), count);
      inv.append(chip);
      this.invChips.set(res, { chip, count });
    }
    const invHead = el('div', 'inv-head');
    invHead.append(el('span', 'card-title', 'Carrying'), this.carryLabel);

    for (const [box, label, value, fill] of [
      [this.taskBox, this.taskLabel, this.taskValue, this.taskFill],
      [this.buildBox, this.buildLabel, this.buildValue, this.buildFill],
    ] as const) {
      const head2 = el('div', 'progress-head');
      head2.append(label, value);
      const track = el('span', 'progress-track');
      track.append(fill);
      box.append(head2, track);
      box.hidden = true;
    }

    const decisionHead = el('header', 'decision-head');
    decisionHead.append(el('h3', undefined, 'This tick’s decision'));
    this.decision.append(decisionHead, this.decisionMeta, this.fallback, this.moveGroup.root, this.actionGroup.root, this.goalGroup.root);

    this.noulsBox.append(el('span', 'card-title', 'Nouls'));
    this.decision.append(this.noulsBox);

    this.stateDetails.append(el('summary', undefined, 'what Jev saw'), this.statePre);
    this.decision.append(this.stateDetails);

    this.root.append(head, traits, body, invHead, inv, this.taskBox, this.buildBox, this.homeLine, this.decision);
    this.update(v);
  }

  update(v: Villager): void {
    const doing = v.asleep
      ? 'Asleep'
      : v.task
        ? (ACTION_DOING[v.task.action] ?? ACTION_LABEL[v.task.action])
        : v.build
          ? 'Building'
          : (v.recent.at(-1) ?? 'Looking around');
    this.activity.textContent = doing.charAt(0).toUpperCase() + doing.slice(1);
    this.goalChip.textContent = GOAL_LABEL[v.goal];

    for (const key of ['hunger', 'energy'] as const) {
      const n = Math.max(0, Math.min(1, v.body[key]));
      const ref = this.bars[key];
      ref.fill.style.width = `${n * 100}%`;
      ref.value.textContent = pct(n);
      ref.track.classList.toggle('is-low', n < 0.25);
    }

    const total = carried(v.inventory);
    this.carryLabel.textContent = `${total}/${v.maxCarry}`;
    this.carryLabel.classList.toggle('is-full', total >= v.maxCarry);
    for (const res of RESOURCES) {
      const ref = this.invChips.get(res)!;
      const n = v.inventory[res];
      ref.count.textContent = String(n);
      ref.chip.classList.toggle('is-empty', n === 0);
    }

    if (v.task && v.task.total > 0) {
      const p = Math.max(0, Math.min(1, v.task.progress / v.task.total));
      this.taskBox.hidden = false;
      this.taskLabel.textContent = ACTION_DOING[v.task.action] ?? ACTION_LABEL[v.task.action];
      this.taskValue.textContent = `${v.task.progress}/${v.task.total} ticks`;
      this.taskFill.style.width = `${p * 100}%`;
    } else {
      this.taskBox.hidden = true;
    }

    if (v.build) {
      const p = v.build.total > 0 ? Math.max(0, Math.min(1, v.build.placed / v.build.total)) : 0;
      this.buildBox.hidden = false;
      this.buildLabel.textContent = `Building a ${(BLUEPRINT_LABEL[v.build.blueprint] ?? v.build.blueprint).toLowerCase()}`;
      this.buildValue.textContent = `${v.build.placed}/${v.build.total} blocks`;
      this.buildFill.style.width = `${p * 100}%`;
    } else {
      this.buildBox.hidden = true;
    }

    const atHome = Math.round(v.x) === v.home.x && Math.round(v.z) === v.home.z;
    this.homeLine.textContent = atHome ? `Home: right here (${v.home.x}, ${v.home.z})` : `Home: ${v.home.x}, ${v.home.z}`;

    const heading = asVillager(v).heading;
    const headingText = heading ? headingLabel(heading) : null;
    this.heading.textContent = headingText ? `→ ${headingText}` : '';
    this.heading.hidden = !headingText;

    this.updateDecision(asDecision(v.lastDecision));
  }

  private updateDecision(d: DecisionView | undefined): void {
    if (!d) {
      this.decision.hidden = true;
      return;
    }
    this.decision.hidden = false;

    this.decisionMeta.replaceChildren();
    const src = el('span', 'tag', d.source);
    src.dataset.source = d.source;
    this.decisionMeta.append(
      src,
      el('span', 'meta-dot', '·'),
      el('span', undefined, `${MOVE_LABEL[d.move]} · ${ACTION_LABEL[d.action]}`),
      el('span', 'meta-dot', '·'),
      el('span', undefined, `${d.latencyMs} ms`),
    );
    this.fallback.hidden = !d.fellBack;

    this.moveGroup.update(d.moveProbs as Record<string, number>, d.move, (k) => MOVE_LABEL[k as Move] ?? k, d.moveConfidence, MOVES, wasSampled(d, 'move'));
    this.actionGroup.update(d.actionProbs as Record<string, number>, d.action, (k) => ACTION_LABEL[k as Action] ?? k, d.actionConfidence, ACTIONS, wasSampled(d, 'action'));
    this.goalGroup.update(d.goalProbs as Record<string, number>, d.goal, (k) => GOAL_LABEL[k as Goal] ?? k, d.goalConfidence, GOALS, wasSampled(d, 'goal'));

    // the Noul set is open — render whatever arrived, in a stable order
    const keys = noulKeys(d.nouls);
    for (const [key, gauge] of this.gauges) {
      if (keys.includes(key)) continue;
      gauge.root.remove();
      this.gauges.delete(key);
    }
    for (const key of keys) {
      let gauge = this.gauges.get(key);
      if (!gauge) {
        gauge = new Gauge(noulLabel(key));
        this.gauges.set(key, gauge);
      }
      gauge.set(d.nouls?.[key] ?? 0);
      this.noulsBox.append(gauge.root); // keeps the order stable
    }
    this.noulsBox.hidden = keys.length === 0;

    const json = safeJson(d.state);
    if (this.statePre.textContent !== json) this.statePre.textContent = json;
  }
}

function safeJson(state: unknown): string {
  try {
    return JSON.stringify(state, null, 2) ?? 'nothing';
  } catch {
    return 'could not be shown';
  }
}
